import React, {
  FC,
  useState,
  forwardRef,
  ReactNode,
  ElementType,
  Children,
  cloneElement,
  useMemo,
  Fragment,
  isValidElement,
} from 'react';
import {
  AppBar,
  AppBarProps,
  BackdropProps,
  Box,
  BoxProps,
  Drawer,
  DrawerProps,
  PaperProps,
  Toolbar,
  ToolbarProps,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { MenuIcon } from '@/src/components/icon/stroke/MenuIcon';
import { CloseIcon } from '@/src/components/icon/stroke/CloseIcon';
import { getFocusRingStyle } from '@/src/components/focusring/focusring';
import { HeaderButton, HeaderButtonProps } from './HeaderButton';
import { HeaderLanguageSelect } from './HeaderLanguageSelect';
import { HeaderNavigationLink } from './HeaderNavigationLink';
import { HeaderNavigationHero } from './HeaderNavigationHero';
import { HeaderNavigationMenu } from './HeaderNavigationMenu';

export interface HeaderProps extends Omit<AppBarProps, 'color'> {
  /**
   * Logo component
   */
  logo?: ReactNode;
  /**
   * Wrap the navigation into container
   * @default false
   */
  inContainer?: boolean;
  /**
   * ActionButtons on the right side
   */
  buttons?: ReactNode;
  /**
   * ActionButtons on the right side that stays visible even on mobile
   */
  visibleButtons?: ReactNode | true;
  /**
   * Controls if mobile version of Header should be shown, this collapses children into mobile menu
   *
   * @default undefined, leads to usage useMediaQuery(theme.breakpoints.down('lg'))
   */
  mobile?: boolean;
  /**
   * Controls open/close state of mobile menu
   */
  open?: boolean;
  /**
   * Function to toggle open state of menu
   */
  setOpen?: (open: boolean) => void;
  /**
   * Properties applied to toolbar container
   */
  toolbarProps?: ToolbarProps<ElementType>;
  /**
   * Properties applied to logo wrapper
   */
  logoProps?: BoxProps<ElementType>;
  /**
   * Properties applied to content wrapper
   */
  contentProps?: BoxProps<ElementType>;
  /**
   * Properties applied to content wrapper in drawer
   */
  mobileContentProps?: BoxProps<ElementType>;
  /**
   * Properties applied to button wrapper
   */
  buttonsProps?: BoxProps<ElementType>;
  /**
   * Properties applied to hamburger menu
   */
  menuButtonProps?: HeaderButtonProps;
  /**
   * Icon of the hamburger menu
   */
  menuIcon?: ReactNode;
  /**
   * Icon of the close hamburger menu
   */
  closeMenuIcon?: ReactNode;
  /**
   * Props applied to mobile drawer
   */
  drawerProps?: DrawerProps;
}

/**
 * Component to render Header with navigation and action buttons at the top of the page
 */
export const Header: FC<HeaderProps> = forwardRef(
  (
    {
      children,
      logo,
      buttons,
      visibleButtons,
      mobile: propMobile,
      open: propOpen,
      setOpen: propSetOpen,
      inContainer = false,
      toolbarProps,
      logoProps,
      contentProps,
      mobileContentProps,
      buttonsProps,
      menuButtonProps,
      menuIcon,
      closeMenuIcon,
      drawerProps,
      ...rest
    },
    ref,
  ) => {
    const theme = useTheme();
    const downLg = useMediaQuery(theme.breakpoints.down('lg'));
    // determine if mobile condition is controlled or default downLg
    const mobile = propMobile === undefined ? downLg : propMobile;
    const { sx: logoSx, ...restLogo } = logoProps ?? {};
    const { sx: contentSx, ...restContent } = contentProps ?? {};
    const { sx: mobileContentSx, ...restMobileContent } = mobileContentProps ?? {};
    const { sx: buttonsSx, ...restButtons } = buttonsProps ?? {};
    const { sx: drawerSx, slotProps: drawerSlotProps, ...restDrawer } = drawerProps ?? {};
    const { paper: drawerPaperProps, backdrop: drawerBackdropProps, ...restDrawerSlotProps } = drawerSlotProps ?? {};
    const { sx: drawerPaperSx, ...restDrawerPaper } = (drawerPaperProps as PaperProps) ?? {};
    const { sx: drawerBackdropSx, ...restDrawerBackdrop } = (drawerBackdropProps as BackdropProps) ?? {};
    const [rawOpen, rawSetOpen] = useState(false);
    const open = propOpen ?? rawOpen;
    const setOpen: React.Dispatch<React.SetStateAction<boolean>> = propSetOpen ?? rawSetOpen;

    /**
     * Process child nodes
     */
    const [processedChildren, processedButtons, drawerButtons] = useMemo(() => {
      function flatten(nodes, extend, keyid) {
        const result = [];
        Children.forEach(nodes, (child, idx) => {
          if (child?.type === Fragment) {
            result.push(...flatten(child.props.children, extend, `${keyid}-n`));
          } else if (
            isValidElement(child) &&
            [
              HeaderButton,
              HeaderLanguageSelect,
              HeaderNavigationLink,
              HeaderNavigationHero,
              HeaderNavigationMenu,
            ].includes(child?.type as FC) &&
            extend
          ) {
            result.push(
              cloneElement(child, {
                extend: true,
                key: `${keyid}-${idx}`,
              } as HeaderButtonProps),
            );
          } else {
            if (typeof child === 'string') {
              result.push(child);
            } else {
              result.push(cloneElement(child, { key: `${keyid}-${idx}` }));
            }
          }
        });
        return result?.length ? result : undefined;
      }

      const flattenedChildren = flatten(children, mobile, 'children');
      // check if we have visibleButtons property
      if (visibleButtons) {
        const flattenedVisibleButtons =
          visibleButtons === true ? undefined : flatten(visibleButtons, false, 'visibleButtons');
        const flattenedRestButtons = flatten(buttons, mobile, 'restButton');
        // mobile
        if (mobile) {
          // split visible buttons and put buttons in drawer
          return [flattenedChildren, flattenedVisibleButtons, flattenedRestButtons];
        } else {
          // on PC, all buttons are visible
          return [flattenedChildren, [...(flattenedVisibleButtons || []), ...(flattenedRestButtons || [])], undefined];
        }
      } else {
        // in case visibleButtons property is not provided, do not put buttons into drawer at all
        return [flattenedChildren, flatten(buttons, false, 'buttons'), undefined];
      }
    }, [children, visibleButtons, buttons, mobile]);

    return (
      <>
        <AppBar ref={ref} data-testid="Header" {...rest}>
          <Toolbar disableGutters={!inContainer} {...toolbarProps}>
            {logo && (
              <Box
                data-testid="Header.logo"
                sx={{
                  color: theme.palette.mode === 'dark' ? 'brand.secondary' : 'brand.tertiary',
                  flexShrink: '0',
                  // restyle wrapper link, same style is applied in HeaderLogo
                  '& .MuiLink-root': {
                    display: 'block',
                    position: 'relative',
                    py: 1,
                    // add proper focus ring
                    ...getFocusRingStyle(theme),
                    // remove default link box shadow
                    '&.Mui-focusVisible': {
                      boxShadow: 'none',
                    },
                  },
                  '& .MuiSvgIcon-root': {
                    display: 'block',
                  },
                  ...logoSx,
                }}
                {...restLogo}
              >
                {logo}
              </Box>
            )}
            <Box
              sx={{
                minWidth: '0',
                flexGrow: '1',
                alignSelf: 'stretch',
                display: 'flex',
                alignItems: 'stretch',
                justifyContent: 'flex-start',
                gap: { lg: 2, xl: 4 },
                ...(!mobile &&
                  logo && {
                    // increase size between logo and nav
                    pl: 4,
                  }),
                // move directly into border
                mb: '-1px',
                ...contentSx,
              }}
              data-testid="Header.nav"
              {...restContent}
            >
              {!mobile && processedChildren}
            </Box>
            {(processedButtons || (mobile && (processedChildren || drawerButtons))) && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 0.25,
                  mr: { xs: -1, lg: 0 },
                  ...buttonsSx,
                }}
                data-testid="Header.btns"
                {...restButtons}
              >
                {processedButtons}
                {mobile && (processedChildren || drawerButtons) && (
                  <HeaderButton
                    key="menu"
                    aria-label="Toggle menu"
                    extend={false}
                    onClick={() => setOpen((prev: boolean) => !prev)}
                    {...menuButtonProps}
                  >
                    {open ? closeMenuIcon || <CloseIcon /> : menuIcon || <MenuIcon />}
                  </HeaderButton>
                )}
              </Box>
            )}
          </Toolbar>
        </AppBar>
        {mobile && (processedChildren || drawerButtons) && (
          <Drawer
            disablePortal
            disableEnforceFocus
            disableAutoFocus
            anchor="top"
            open={open}
            onClose={() => setOpen(false)}
            sx={{
              // bellow Header, above SecondaryNavigation
              zIndex: theme.zIndex.appBar - 10,
              // header offset
              top: theme.mixins.toolbar.minHeight,
              bottom: 'auto',
              height: `calc(100vh - ${theme.mixins.toolbar.minHeight})`,
              // adjust position
              position: 'absolute',
              ...drawerSx,
            }}
            slotProps={{
              backdrop: {
                sx: {
                  position: 'absolute',
                  ...drawerBackdropSx,
                },
                ...restDrawerBackdrop,
              },
              paper: {
                sx: {
                  position: 'absolute',
                  height: '100%',
                  display: 'grid',
                  gridTemplateRows: 'auto max-content',
                  ...drawerPaperSx,
                },
                ...restDrawerPaper,
              },
              ...restDrawerSlotProps,
            }}
            {...restDrawer}
          >
            <Box
              sx={{
                p: 2,
                overflow: 'auto',
                ...mobileContentSx,
              }}
              data-testid="Header.mobilenav"
              {...restMobileContent}
            >
              {processedChildren}
            </Box>
            {drawerButtons && (
              <Box sx={{ p: 2, pt: 0 }}>
                <Box
                  sx={{
                    pt: 0.75,
                    display: 'flex',
                    flexDirection: 'column-reverse',
                    alignItems: 'stretch',
                    justifyContent: 'flex-start',
                    // border from appbar.theme.ts
                    borderTop: 1,
                    borderColor: theme.palette.mode === 'dark' ? 'onSurface.os700' : 'surface.secondary',
                  }}
                >
                  {drawerButtons}
                </Box>
              </Box>
            )}
          </Drawer>
        )}
      </>
    );
  },
);
